import React, { useState } from "react";
import { useAppTranslation } from "@/utils/useAppTranslation";
import {
  Button,
  Col,
  InputNumber,
  LegacyCard,
  LegacyDatePicker,
  LegacyForm,
  Row,
  Select,
  Space,
  Typography,
} from "@payping/web-components";
import { SDKDateObject, isBeforeToday, toJalali } from "@payping/utils/dateUtils";
import type { FormInstance } from "@payping/web-components/legacyComponents/Form/types";
import { SchedulingType } from "@/pages/Shop/Invoice/forms/type";
import { SwitchRow } from "@/core/component";
import { numberFormatter } from "@payping/utilities-shared/number";

interface Props {
  setSentDate: React.Dispatch<React.SetStateAction<string | undefined>>;
  setDueDate: React.Dispatch<React.SetStateAction<string | undefined>>;
  setIsSchedule: React.Dispatch<React.SetStateAction<boolean>>;
  prev: () => void;
  next: () => void;
  loading: boolean;
  form: FormInstance;
}

const SecondStepInvoice: React.FC<Props> = (props: Props) => {
  const { setSentDate, setIsSchedule, setDueDate, prev, next, loading, form } = props;
  const { t } = useAppTranslation();
  const [repeatDates, setRepeatDates] = useState<SDKDateObject[]>([]);

  const handleRepeatDates = (values: any) => {
    const { sendDate, interval, scheduleType, dueDateAfterHowManyDay, count } = values;
    const dates: any = [];

    for (let i = 0; i < count; i += 1) {
      switch (scheduleType) {
        case 0: // روز
          dates.push(toJalali({ date: sendDate, manipulate: [{ args: [interval * i, "day"], method: "add" }] }).object);
          break;
        case 1: // هفته
          dates.push(
            toJalali({ date: sendDate, manipulate: [{ args: [interval * i, "week"], method: "add" }] }).object,
          );
          break;
        case 2: // ماه
          dates.push(
            toJalali({ date: sendDate, manipulate: [{ args: [interval * i, "month"], method: "add" }] }).object,
          );
          break;
        case 3: // سال
          dates.push(
            toJalali({ date: sendDate, manipulate: [{ args: [interval * i, "year"], method: "add" }] }).object,
          );
          break;
        default:
          return undefined;
      }
      // dates.push(sw(dates.length ? dates[dates.length - 1] : sendDate));
    }

    if (values.dueDateAfterHowManyDay) {
      setDueDate(
        toJalali({
          date: sendDate,
          manipulate: [{ args: [dueDateAfterHowManyDay, "day"], method: "add" }],
          toFormat: " jDD jMMMM jYYYY",
        }).string,
      );
    }

    setRepeatDates(dates);
  };

  const onFinish = () => {
    next();
  };
  return (
    <LegacyForm<SchedulingType>
      canDeactivate
      scrollToFirstError={{ behavior: "smooth" }}
      form={form}
      onFinish={onFinish}
      layout={"vertical"}
      initialValues={{
        scheduleType: 0,
        count: 2,
        dueDateAfterHowManyDay: 1,
        interval: 1,
      }}
      onValuesChange={(changedValues, values) => {
        if ("sendDate" in changedValues) {
          setSentDate(changedValues.sendDate?.locale("fa").format(" jDD jMMMM jYYYY"));
        }
        if ("dueDate" in changedValues) {
          setDueDate(changedValues.dueDate?.locale("fa").format(" jDD jMMMM jYYYY"));
        }
        if ("dueDateAfterHowManyDay" in changedValues) {
          setDueDate(changedValues.dueDateAfterHowManyDay?.toString());
        }
        if (
          "sendDate" in changedValues ||
          "interval" in changedValues ||
          "scheduleType" in changedValues ||
          "dueDateAfterHowManyDay" in changedValues ||
          "count" in changedValues
        ) {
          handleRepeatDates({
            sendDate: values.sendDate,
            interval: values.interval,
            count: values.count,
            scheduleType: values.scheduleType,
            dueDateAfterHowManyDay: values.dueDateAfterHowManyDay,
          });
        }
      }}
    >
      <LegacyForm.Item
        label={t("shop.createInvoice.secondStep.startDate")}
        rules={[{ required: true }]}
        name="sendDate"
        wrapperCol={{ md: 8 }}
      >
        <LegacyDatePicker className="tw:w-full" disabledDate={(date) => isBeforeToday(date)} skeleton={loading} />
      </LegacyForm.Item>
      <LegacyForm.Item valuePropName={"checked"} name={"isSchedule"}>
        <SwitchRow
          onChange={(checked) => {
            if (checked) setIsSchedule(true);
            else {
              setIsSchedule(false);
            }
          }}
          label={t("shop.createInvoice.secondStep.repeat")}
          loading={loading}
        />
      </LegacyForm.Item>
      <LegacyForm.Item noStyle dependencies={["isSchedule"]}>
        {({ getFieldValue }) => {
          const isRepeat = getFieldValue("isSchedule");
          return (
            isRepeat && (
              <>
                <Row gutter={[8, 8]}>
                  <Col xs={6}>
                    <LegacyForm.Item label={t("invoice.create.base.repeatIn")} rules={[{ required: true }]}>
                      <Space.Compact>
                        <LegacyForm.Item name="interval" noStyle>
                          <InputNumber
                            formatter={(value) => numberFormatter.format(value, { maxDecimals: 0 })}
                            parser={(value) => numberFormatter.parse(value)}
                            disabled={loading}
                            controls={false}
                            inputMode="numeric"
                            changeOnWheel={false}
                            className="tw:w-full!"
                          />
                        </LegacyForm.Item>
                        <LegacyForm.Item name="scheduleType" noStyle>
                          <Select disabled={loading}>
                            <Select.Option value={0}>{t("invoice.create.base.day")}</Select.Option>
                            <Select.Option value={1}>{t("invoice.create.base.week")}</Select.Option>
                            <Select.Option value={2}>{t("invoice.create.base.month")}</Select.Option>
                            <Select.Option value={3}>{t("invoice.create.base.year")}</Select.Option>
                          </Select>
                        </LegacyForm.Item>
                      </Space.Compact>
                    </LegacyForm.Item>
                  </Col>
                  <Col xs={6}>
                    <LegacyForm.Item label={t("invoice.create.base.invoiceDueDate")} name="dueDateAfterHowManyDay">
                      <InputNumber
                        formatter={(value) => numberFormatter.format(value, { maxDecimals: 0 })}
                        parser={(value) => numberFormatter.parse(value)}
                        disabled={loading}
                        controls={false}
                        inputMode="numeric"
                        changeOnWheel={false}
                        className="tw:w-full!"
                      />
                    </LegacyForm.Item>
                  </Col>
                  <Col xs={6}>
                    <LegacyForm.Item
                      label={t("invoice.create.base.count")}
                      name="count"
                      rules={[
                        {
                          validator: (_rule, value): Promise<void> =>
                            value && Number.isInteger(+value) && +value > 1
                              ? Promise.resolve()
                              : Promise.reject(t("error.scheduleCount")),
                        },
                      ]}
                    >
                      <InputNumber
                        formatter={(value) => numberFormatter.format(value, { maxDecimals: 0 })}
                        parser={(value) => numberFormatter.parse(value)}
                        disabled={loading}
                        controls={false}
                        inputMode="numeric"
                        changeOnWheel={false}
                        className="tw:w-full!"
                      />
                    </LegacyForm.Item>
                  </Col>
                </Row>
                <LegacyForm.Item>
                  {repeatDates.length > 0 && (
                    <Row gutter={[8, 8]}>
                      {repeatDates.map((date, index) => (
                        <Col key={index} xs={12} sm={8} md={6} lg={4} xl={4}>
                          <LegacyCard.Ant loading={loading}>
                            <Typography.Text>{date.format("jDD jMMMM")}</Typography.Text>
                          </LegacyCard.Ant>
                        </Col>
                      ))}
                    </Row>
                  )}
                </LegacyForm.Item>
              </>
            )
          );
        }}
      </LegacyForm.Item>
      <LegacyForm.Item noStyle dependencies={["isSchedule"]}>
        {({ getFieldValue }) => {
          const showDueDate = getFieldValue("isSchedule");
          return (
            !showDueDate && (
              <LegacyForm.Item
                label={t("invoice.create.base.dueDate")}
                rules={[{ required: true }]}
                name="dueDate"
                wrapperCol={{ md: 8 }}
                dependencies={["isSchedule"]}
              >
                <LegacyDatePicker className="tw:w-full" disabledDate={(date) => isBeforeToday(date)} />
              </LegacyForm.Item>
            )
          );
        }}
      </LegacyForm.Item>
      <LegacyForm.Item>
        <Row gutter={[8, 8]}>
          <Col flex={2}>
            <Button block type={"primary"} htmlType={"submit"}>
              {t("shop.createInvoice.nextStep")}
            </Button>
          </Col>
          <Col flex={1}>
            <Button block onClick={() => prev()}>
              {t("back")}
            </Button>
          </Col>
        </Row>
      </LegacyForm.Item>
    </LegacyForm>
  );
};

export default SecondStepInvoice;
